import { useState, useEffect } from 'react';
import { useApiUrl, useCustom } from '@refinedev/core';
import { getIdToken } from '@/providers/auth-provider';
import { toast } from '@/hooks/useToast';
import { X, Gift, AlertCircle, Loader2 } from 'lucide-react';
import { chunkGrantBodies, postGrantChunks } from '@/lib/grant-chunks';

interface ActivePromotion {
  id: string;
  name: string;
  source: string;
  paywall_type: string;
  coupons_per_claim: number;
  shareable_code: string | null;
  max_total_claims: number | null;
}

interface ListResponse {
  rows: ActivePromotion[];
}

export interface GrantResult {
  granted: number;
  skipped: { user_id: string; reason: string }[];
  claim_ids: string[];
}

export interface GrantPromotionModalProps {
  open: boolean;
  onClose: () => void;
  onGranted?: (result: GrantResult) => void;
  /** Pre-fill user_ids — for User Detail page (1 user) or saved-segment flows */
  presetUserIds?: string[];
  /** Pre-select a promotion — for Promotion Detail page's inline grant */
  presetPromotionId?: string;
  /** Lock the user_ids field (used when called from User Detail with a single userId) */
  lockUserIds?: boolean;
  /** Lock the promotion field (used when called from Promotion Detail) */
  lockPromotion?: boolean;
}

export function GrantPromotionModal({
  open,
  onClose,
  onGranted,
  presetUserIds = [],
  presetPromotionId,
  lockUserIds = false,
  lockPromotion = false,
}: GrantPromotionModalProps) {
  const apiUrl = useApiUrl();
  const [promotionId, setPromotionId] = useState<string>(presetPromotionId ?? '');
  const [userIdsText, setUserIdsText] = useState<string>(presetUserIds.join('\n'));
  const [reason, setReason] = useState<string>('');
  const [confirmBulk, setConfirmBulk] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when re-opened
  useEffect(() => {
    if (open) {
      setPromotionId(presetPromotionId ?? '');
      setUserIdsText(presetUserIds.join('\n'));
      setReason('');
      setConfirmBulk('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetPromotionId, presetUserIds.join('|')]);

  const { data: promosData } = useCustom<ListResponse>({
    url: '/v1/admin/promotions',
    method: 'get',
    config: { query: { status: 'active', limit: '200' } },
  });
  const promotions = promosData?.data?.rows ?? [];

  const userIds = userIdsText
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const isBulk = userIds.length > 100;
  const reasonValid = reason.trim().length >= 3;
  const confirmValid = !isBulk || confirmBulk === 'GRANT';
  const canSubmit =
    promotionId && userIds.length > 0 && reasonValid && confirmValid && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const idToken = await getIdToken();
      // Chunks under the API WAF's 8 KB body limit (lib/grant-chunks.ts): one request
      // for up to ~180 users, more for a large segment. The typed "GRANT" confirmation
      // (confirmValid above) covers the whole grant and is sent on every chunk.
      const chunks = chunkGrantBodies(promotionId, userIds, reason.trim(), { confirmBulk: isBulk });
      const sent = await postGrantChunks(`${apiUrl}/v1/admin/coupons/grant`, idToken, chunks);
      if (sent.error) {
        // Some users may already be granted: say exactly what happened. Sending the
        // same grant again is safe (users who already have it are skipped).
        if (sent.granted > 0) onGranted?.({ granted: sent.granted, skipped: sent.skipped, claim_ids: sent.claim_ids });
        throw new Error(
          `${sent.error}. ${sent.granted} granted before it; ${sent.unconfirmed} not confirmed. Grant again to finish: users who already have it are skipped.`,
        );
      }
      const result: GrantResult = { granted: sent.granted, skipped: sent.skipped, claim_ids: sent.claim_ids };
      const skippedSummary =
        result.skipped.length ? `, ${result.skipped.length} skipped` : '';
      toast({
        title: 'Coupons granted',
        description: `${result.granted} of ${userIds.length} user${userIds.length === 1 ? '' : 's'}${skippedSummary}.`,
        variant: result.skipped.length > 0 ? 'warning' : 'success',
      });
      onGranted?.(result);
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b p-5">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Gift className="h-5 w-5" />
            Grant Promotion
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-muted-foreground hover:text-foreground disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 p-5">
          {/* Promotion selector */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Promotion <span className="text-destructive">*</span>
            </label>
            <select
              value={promotionId}
              onChange={(e) => setPromotionId(e.target.value)}
              disabled={lockPromotion || submitting}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm disabled:opacity-50"
            >
              <option value="">Select an active promotion…</option>
              {promotions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.source}, {p.coupons_per_claim} per claim
                  {p.shareable_code ? ` (code: ${p.shareable_code})` : ''}
                </option>
              ))}
            </select>
            {promotions.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                No active promotions.{' '}
                <a href="/promotions/new" className="underline">
                  Create one
                </a>
                .
              </p>
            )}
          </div>

          {/* Target user IDs */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Target user IDs <span className="text-destructive">*</span>
            </label>
            <textarea
              rows={lockUserIds ? 2 : 5}
              value={userIdsText}
              onChange={(e) => setUserIdsText(e.target.value)}
              disabled={lockUserIds || submitting}
              placeholder="One UUID per line, or comma/space separated"
              className="w-full rounded-lg border bg-background px-3 py-2 font-mono text-sm disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {userIds.length} user{userIds.length === 1 ? '' : 's'} parsed.
              {isBulk && (
                <span className="ml-1 text-amber-600">
                  Bulk grant — typed confirmation required below.
                </span>
              )}
            </p>
          </div>

          {/* Reason */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Reason <span className="text-destructive">*</span>
            </label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              placeholder="e.g. tester onboarding / support comp ticket #1234 / Black Friday campaign"
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Required (≥ 3 chars). Persisted to admin_audit_log.
            </p>
          </div>

          {/* Bulk typed confirmation */}
          {isBulk && (
            <div>
              <label className="mb-1 block text-sm font-medium text-amber-600">
                Type GRANT to confirm <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={confirmBulk}
                onChange={(e) => setConfirmBulk(e.target.value)}
                disabled={submitting}
                placeholder="GRANT"
                className="w-full rounded-lg border-2 border-amber-300 bg-background px-3 py-2 font-mono text-sm uppercase disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Granting to &gt; 100 users. Type the word GRANT (uppercase) to enable submit.
              </p>
            </div>
          )}

          {/* Inline error */}
          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
              <div className="text-destructive">{error}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t p-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting
              ? 'Granting…'
              : `Grant${userIds.length > 0 ? ` ${userIds.length} user${userIds.length === 1 ? '' : 's'}` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
